import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button, Modal, Form } from 'react-bootstrap';
import { AiOutlineFileAdd } from "react-icons/ai";
import { BsPencilFill, BsFileEarmarkCode } from "react-icons/bs";
import { LuFilter, LuX } from 'react-icons/lu';
import { GrAttachment, GrInbox } from "react-icons/gr";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import { IoSettingsOutline, IoWarningOutline } from "react-icons/io5";
import axios from 'axios';
import { useCurrency } from '../../context/currencyContext.js';

const API_URL = process.env.REACT_APP_API_URL || 'https://hms.backend.hiragroupcloud.com';

// Function to retrieve userLabId from localStorage by searching auth keys and all items
const getUserLabId = () => {
  try {
    // Try to get from auth-related keys
    const authKeys = ['user', 'auth', 'userData', 'currentUser', 'profile'];
   
    for (const key of authKeys) {
      const value = localStorage.getItem(key);
      if (value) {
        try {
          const parsed = JSON.parse(value);
          // Check for labId in various possible locations
          const labId = parsed.labId || parsed.userLabId || parsed.lab_id ||
                        parsed.lab?.id || parsed.lab?.labId ||
                        parsed.user?.labId || parsed.user?.userLabId;
         
          if (labId && /^000000\d{2}$/.test(labId)) {
            return labId;
          }
        } catch (e) {
          // Not JSON, continue
        }
      }
    }
   
    // Search all localStorage items
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      const value = localStorage.getItem(key);
     
      // Skip if value is null or undefined
      if (!value) continue;
     
      // Check if value is exactly a labId
      if (/^000000\d{2}$/.test(value.trim())) {
        return value.trim();
      }
     
      // Try to parse as JSON
      try {
        const parsed = JSON.parse(value);
       
        // Recursive function to find labId in object
        const findLabId = (obj, path = '') => {
          if (!obj || typeof obj !== 'object') return null;
         
          // Check all string properties
          for (const prop in obj) {
            const val = obj[prop];
            const currentPath = path ? `${path}.${prop}` : prop;
           
            // If value is string and matches labId pattern
            if (typeof val === 'string' && /^000000\d{2}$/.test(val)) {
              return val;
            }
           
            // If value is object, search recursively
            if (val && typeof val === 'object') {
              const found = findLabId(val, currentPath);
              if (found) return found;
            }
          }
          return null;
        };
       
        const foundId = findLabId(parsed);
        if (foundId) {
          return foundId;
        }
      } catch (e) {
        // Not JSON, continue
      }
    }
   
    return null;
   
  } catch (error) {
    return null;
  }
};

const CasesOpened = () => {
  const { currency, formatAmount } = useCurrency();
  const [allCases, setAllCases] = useState([]); // All cases from API
  const [cases, setCases] = useState([]); // Filtered cases
  const [accounts] = useState([]);
  const [dropdownVisible, setDropdownVisible] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [monthlyTotal, setMonthlyTotal] = useState(0);
  const [monthlyInvoicedTotal, setMonthlyInvoicedTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userLabId, setUserLabId] = useState(null);
  const [noLabSelected, setNoLabSelected] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { accountId, accountName } = location.state || {};
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [paginatedCases, setPaginatedCases] = useState([]);
  
  // Filter states
  const [filters, setFilters] = useState({
    filterType: 'account',
    filterValue: '',
    by: 'month',
    caseTag: 'any',
    properties: 'all',
    startDate: '',
    endDate: '',
    caseStatus: ''
  });
  // Derived filter options from cases data
  const [filterOptions, setFilterOptions] = useState({
    accounts: [],
    providers: [],
    patients: [],
    caseIds: [],
    deliveryDates: [],
    caseTags: [],
    statuses: []
  });

  // Function to format invoice number by padding with zeros
  const formatInvoiceNumber = (number) => {
    return number ? String(number).padStart(6, '0') : 'N/A';
  };

  // Function to format date string to locale date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-GB');
    } catch (error) {
      return '';
    }
  };

  // Function to fetch invoice details for a given invoice number
  const fetchInvoiceDetails = async (invoiceNumber) => {
    try {
      const response = await axios.get(`${API_URL}/api/invoices/${invoiceNumber}`);
      return response.data;
    } catch (error) {
      return null;
    }
  };

  // Helper function to extract labId from a case item
  const getCaseLabId = (caseItem) => {
    // Check multiple possible locations for labId
    return caseItem.userLabId ||
           caseItem.addedBy?.labId ||
           caseItem.addedBy?.userLabId ||
           caseItem.labId;
  };

  // Function to filter cases by userLabId
  const filterByLabId = (casesToFilter) => {
    if (!userLabId) return casesToFilter;
   
    const labFiltered = casesToFilter.filter(caseItem => {
      const caseLabId = getCaseLabId(caseItem);
      return caseLabId === userLabId;
    });
   
    return labFiltered;
  };

  // Function to extract unique filter options from cases data
  const extractFilterOptions = (casesData) => {
    const accounts = new Set();
    const providers = new Set();
    const patients = new Set();
    const caseIds = new Set();
    const deliveryDates = new Set();
    const caseTags = new Set();
    const statuses = new Set();

    casesData.forEach(caseItem => {
      if (caseItem.account_name && caseItem.account_name !== 'No account assigned') {
        accounts.add(caseItem.account_name);
      } else if (caseItem.account_holder) {
        accounts.add(caseItem.account_holder);
      }
      if (caseItem.provider_name && caseItem.provider_name !== 'No provider assigned') {
        providers.add(caseItem.provider_name);
      }
      if (caseItem.last_name) {
        patients.add(caseItem.last_name);
      } else if (caseItem.first_name) {
        patients.add(caseItem.first_name);
      }
      if (caseItem.caseNumber) {
        caseIds.add(caseItem.caseNumber);
      } else if (caseItem.case_id) {
        caseIds.add(caseItem.case_id);
      }
      if (caseItem.delivery_date) {
        const formattedDate = formatDate(caseItem.delivery_date);
        if (formattedDate) {
          deliveryDates.add(formattedDate);
        }
      }
      if (caseItem.tags && Array.isArray(caseItem.tags)) {
        caseItem.tags.forEach(tag => caseTags.add(tag));
      }
      if (caseItem.status) {
        statuses.add(caseItem.status);
      }
    });

    const options = {
      accounts: Array.from(accounts).sort().map(account => ({ value: account, label: account })),
      providers: Array.from(providers).sort().map(provider => ({ value: provider, label: provider })),
      patients: Array.from(patients).sort().map(patient => ({ value: patient, label: patient })),
      caseIds: Array.from(caseIds).sort().map(caseId => ({ value: caseId, label: caseId })),
      deliveryDates: Array.from(deliveryDates).sort().map(date => ({ value: date, label: date })),
      caseTags: Array.from(caseTags).sort(),
      statuses: Array.from(statuses).sort()
    };

    return options;
  };

  // Effect to get userLabId on component mount
  useEffect(() => {
    const labId = getUserLabId();
    
    if (labId) {
      setUserLabId(labId);
      setNoLabSelected(false);
    } else {
      setNoLabSelected(true);
      setUserLabId(null);
      setLoading(false);
    }
  }, []);

  // Effect to fetch cases based on userLabId and accountId
  useEffect(() => {
    // Don't fetch if no lab is selected
    if (noLabSelected) {
      setLoading(false);
      return;
    }
    
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
       
        // Build query parameters
        const params = {};
       
        // Add userLabId to params if it exists
        if (userLabId) {
          params.userLabId = userLabId;
        }
       
        // Add accountId if provided
        if (accountId) {
          params.accountId = accountId;
        }
       
        // Fetch cases from API
        const response = await axios.get(`${API_URL}/api/cases/cases`, {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true,
          params: params
        });
       
        if (response.data.success) {
          const casesData = response.data.data || [];
         
          // Store all cases
          setAllCases(casesData);
         
          // Fetch invoice details for cases that have invoice numbers
          const casesWithInvoices = await Promise.all(
            casesData.map(async (caseItem) => {
              if (caseItem.invoiceNumber) {
                const invoiceData = await fetchInvoiceDetails(caseItem.invoiceNumber);
                return { ...caseItem, invoiceData };
              }
              return caseItem;
            })
          );
         
          // Extract initial filter options from all cases
          const initialFilterOptions = extractFilterOptions(casesWithInvoices);
          setFilterOptions(initialFilterOptions);
         
        } else {
          setError(response.data.message || 'Failed to load cases.');
        }
      } catch (error) {
        if (error.response) {
          setError(`Server error: ${error.response.status} - ${error.response.data?.message || 'Unknown error'}`);
        } else if (error.request) {
          setError('No response from server. Please check your connection.');
        } else {
          setError('Failed to load cases. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    // Fetch data when userLabId or accountId changes
    if (userLabId !== undefined && !noLabSelected) {
      fetchData();
    }
  }, [userLabId, accountId, noLabSelected]);

  // Function to get current filter options based on selected filter type
  const getCurrentFilterOptions = () => {
    switch (filters.filterType) {
      case 'account':
        return filterOptions.accounts;
      case 'provider':
        return filterOptions.providers;
      case 'patient':
        return filterOptions.patients;
      case 'caseId':
        return filterOptions.caseIds;
      case 'delivery':
        return filterOptions.deliveryDates;
      default:
        return [];
    }
  };

  // Function to check if a case should be included based on non-date filters
  const shouldIncludeCase = (caseItem) => {
    // Main filter value
    if (filters.filterValue) {
      switch (filters.filterType) {
        case 'account':
          if (caseItem.account_name !== filters.filterValue && caseItem.account_holder !== filters.filterValue) return false;
          break;
        case 'provider':
          if (caseItem.provider_name !== filters.filterValue) return false;
          break;
        case 'patient':
          const patientName = caseItem.last_name || caseItem.first_name;
          if (patientName !== filters.filterValue) return false;
          break;
        case 'caseId':
          const caseId = caseItem.caseNumber || caseItem.case_id;
          if (caseId !== filters.filterValue) return false;
          break;
        case 'delivery':
          if (formatDate(caseItem.delivery_date) !== filters.filterValue) return false;
          break;
        default:
          break;
      }
    }
    // Case tag filtering
    if (filters.caseTag !== 'any' && (!caseItem.tags || !caseItem.tags.includes(filters.caseTag))) {
      return false;
    }
    // Case status filtering
    if (filters.caseStatus && caseItem.status !== filters.caseStatus) {
      return false;
    }
    // Properties filtering
    if (filters.properties !== 'all') {
      const hasDigital = caseItem.procedures?.some(p => p.category === 'digital');
      const hasEnclosures = caseItem.procedures?.some(p => p.category === 'enclosure');
      if (filters.properties === 'digital' && !hasDigital) return false;
      if (filters.properties === 'enclosures' && !hasEnclosures) return false;
      if (filters.properties === 'both' && (!hasDigital || !hasEnclosures)) return false;
    }
    return true;
  };

  // Function to apply all filters including labId and date filters
  const applyAllFilters = (casesToFilter) => {
    // First filter by labId
    let filtered = filterByLabId(casesToFilter);
   
    // Then apply other filters
    filtered = filtered.filter((caseItem) => {
      // Apply non-date filters
      if (!shouldIncludeCase(caseItem)) return false;
      // Date filtering - CASES filtered by CASE DATE only
      if (filters.by === 'month') {
        const caseDate = new Date(caseItem.date || caseItem.createdAt);
        if (isNaN(caseDate.getTime())) return false;
       
        const caseMonth = caseDate.getMonth();
        const caseYear = caseDate.getFullYear();
       
        // Check if case is in the selected month and year
        if (caseMonth !== currentMonth || caseYear !== currentYear) {
          return false;
        }
      } else if (filters.by === 'dateRange') {
        const caseDate = new Date(caseItem.date || caseItem.createdAt);
        if (isNaN(caseDate.getTime())) return false;
       
        if (filters.startDate) {
          const startDate = new Date(filters.startDate);
          if (caseDate < startDate) return false;
        }
       
        if (filters.endDate) {
          const endDate = new Date(filters.endDate);
          endDate.setHours(23, 59, 59, 999);
          if (caseDate > endDate) return false;
        }
      }
      return true;
    });
   
    return filtered;
  };

  // Effect to update filtered cases and filter options
  useEffect(() => {
    if (allCases.length === 0 || noLabSelected) {
      setCases([]);
      setCurrentPage(1);
      return;
    }
    // Apply all filters to get current filtered cases
    const newlyFilteredCases = applyAllFilters(allCases);
    setCases(newlyFilteredCases);
   
    // Reset to first page when filters change
    setCurrentPage(1);
   
    // Extract filter options from the filtered cases
    const newFilterOptions = extractFilterOptions(newlyFilteredCases);
    setFilterOptions(newFilterOptions);
   
  }, [allCases, filters, currentMonth, currentYear, userLabId, noLabSelected]);

  // Effect to handle pagination - only shows cases for current page
  useEffect(() => {
    if (cases.length === 0 || noLabSelected) {
      setPaginatedCases([]);
      return;
    }
    
    // Calculate pagination indices
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    
    // Get cases for current page
    const currentPageCases = cases.slice(indexOfFirstItem, indexOfLastItem);
    setPaginatedCases(currentPageCases);
    
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [cases, currentPage, itemsPerPage, noLabSelected]);

  // Effect to calculate monthly totals for cases and invoiced amounts
  useEffect(() => {
    if (noLabSelected) {
      setMonthlyTotal(0);
      setMonthlyInvoicedTotal(0);
      return;
    }
    
    // Total for all filtered cases (based on case date)
    const total = cases.reduce((sum, caseItem) => {
      const amount = caseItem.totalPrice || caseItem.totalAmount || 0;
      return sum + (typeof amount === 'number' ? amount : parseFloat(amount) || 0);
    }, 0);
    setMonthlyTotal(total);
    // Calculate invoiced total SEPARATELY - based on INVOICE DATE, not case date
    let invoicedTotal = 0;
   
    // First filter all cases by labId for invoice calculation
    const labFilteredCases = filterByLabId(allCases);
   
    // Check lab-filtered cases for invoices in the current month
    labFilteredCases.forEach(caseItem => {
      if (caseItem.invoiceNumber && caseItem.invoiceData && caseItem.invoiceData.invoiceDate) {
        const invoiceDate = new Date(caseItem.invoiceData.invoiceDate);
       
        // Check if invoice date falls within the selected month filter
        if (filters.by === 'month') {
          if (!isNaN(invoiceDate.getTime()) &&
              invoiceDate.getMonth() === currentMonth &&
              invoiceDate.getFullYear() === currentYear) {
            const amount = caseItem.invoiceData?.total || caseItem.totalPrice || caseItem.totalAmount || 0;
            invoicedTotal += (typeof amount === 'number' ? amount : parseFloat(amount) || 0);
          }
        }
        // Check if invoice date falls within custom date range
        else if (filters.by === 'dateRange') {
          let invoiceInRange = true;
         
          if (filters.startDate) {
            const startDate = new Date(filters.startDate);
            if (invoiceDate < startDate) invoiceInRange = false;
          }
         
          if (filters.endDate) {
            const endDate = new Date(filters.endDate);
            endDate.setHours(23, 59, 59, 999);
            if (invoiceDate > endDate) invoiceInRange = false;
          }
         
          if (invoiceInRange) {
            const amount = caseItem.invoiceData?.total || caseItem.totalPrice || caseItem.totalAmount || 0;
            invoicedTotal += (typeof amount === 'number' ? amount : parseFloat(amount) || 0);
          }
        }
      }
    });
   
    setMonthlyInvoicedTotal(invoicedTotal);
  }, [cases, allCases, filters, currentMonth, currentYear, userLabId, noLabSelected]);

  // Pagination helper functions
  const totalPages = Math.ceil(cases.length / itemsPerPage);
  
  const handleFirstPage = () => {
    setCurrentPage(1);
  };
  
  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };
  
  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };
  
  const handleLastPage = () => {
    setCurrentPage(totalPages);
  };

  // Generate page numbers to display (shows 5 page numbers at a time)
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
      let endPage = startPage + maxPagesToShow - 1;
      
      if (endPage > totalPages) {
        endPage = totalPages;
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
    }
    
    return pageNumbers;
  };

  // Handler for filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
   
    // If filter type changes, reset filter value
    if (name === 'filterType') {
      setFilters(prev => ({
        ...prev,
        filterType: value,
        filterValue: ''
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handler to apply filters and close modal
  const handleApplyFilters = () => {
    setShowFilterModal(false);
  };

  // Handler to reset all filters
  const handleResetFilters = () => {
    setFilters({
      filterType: 'account',
      filterValue: '',
      by: 'month',
      caseTag: 'any',
      properties: 'all',
      startDate: '',
      endDate: '',
      caseStatus: ''
    });
  };

  // Handler to toggle dropdown for case actions
  const toggleDropdown = (caseId) => {
    setDropdownVisible(dropdownVisible === caseId ? null : caseId);
  };

  // Handler to navigate to previous month
  const handlePreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
    setDropdownVisible(null);
  };

  // Handler to navigate to next month
  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
    setDropdownVisible(null);
  };

  // Handler for account click navigation
  const handleAccountClick = (accountName) => {
    const account = accounts.find(acc => acc.name === accountName);
    if (account) {
      navigate('/TabsWorking', { state: { accountName: accountName, accountData: account } });
    }
  };

  const monthName = new Date(currentYear, currentMonth).toLocaleString('default', { month: 'long' });

  // If no lab is selected, show message
  if (noLabSelected) {
    return (
        <div style={{textAlign: 'center',padding: '50px'}}>
            <h4> Please select the lab to view cases entries for the specific lab</h4>
          </div>
      );
  }

  if (loading) {
    return (
      <div style={{ marginTop: '20px', width: '95%', margin: '0 auto', padding: "20px" }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span style={{ marginLeft: '10px' }}>
            {userLabId ? `Loading cases for lab: ${userLabId}` : 'Loading cases...'}
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ marginTop: '20px', width: '95%', margin: '0 auto', padding: "20px" }}>
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error Loading Cases</h4>
          <p>{error}</p>
          <hr />
          <p className="mb-0">
            {userLabId ? `Lab ID: ${userLabId}` : 'No lab selected'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={{ marginTop: '20px', margin: '0 auto', padding: "20px" }}>
        {/* Header with Monthly Total */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 className="title" style={{ margin: 0, backgroundColor: "white" }}>
            {accountName
              ? `All Cases for ${accountName}`
              : `Cases Opened ${monthName} ${currentYear} (${formatAmount(monthlyTotal)})`}
            <br />
            <small style={{ fontSize: '14px', color: '#666', fontWeight: 'normal' }}>
              {userLabId ? `Showing cases for Lab: ${userLabId}` : 'No lab selected'}
            </small>
          </h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={handlePreviousMonth} style={{ padding: '10px', backgroundColor: '#004aad', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>
              <MdChevronLeft style={{ fontSize: "20px" }} /> Previous Month
            </button>
            <button onClick={handleNextMonth} style={{ padding: '10px', backgroundColor: '#004aad', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>
              Next Month <MdChevronRight style={{ fontSize: "20px" }} />
            </button>
          </div>
        </div>
        {/* Invoiced Cases Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 className="title" style={{ margin: 0, backgroundColor: "white", color: "#004aad" }}>
            {accountName
              ? `All Cases for ${accountName}`
              : `Invoiced in ${monthName} ${currentYear} (${formatAmount(monthlyInvoicedTotal)})`}
          </h3>
        </div>
        {/* Filter Button */}
        <div className="col-md-0 d-flex justify-content-end">
          <button
            style={{ padding: '5px', backgroundColor: '#004aad', color: 'white', width: '90px', height: '35px', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
            onClick={() => setShowFilterModal(true)}
          >
            <LuFilter /> Filters
          </button>
        </div>
        <br /><br />
        {/* Cases Table */}
        <div style={{ width: "100%", margin: "0 auto", padding: "20px", border: "3px solid #f4f4f4", borderRadius: "5px", overflowX: 'auto' }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: '800px' }}>
            {/* Table Headers */}
            <thead>
              <tr>
                <th style={{ padding: "10px", textAlign: "left", borderBottom: "1px solid #ddd" }}><input type="checkbox" /></th>
                {["Inbox", "Attachment", "Case/Pan", "Invoice", "Account", "Provider", "Patient/Appt", "Account Ship", "Lab Ship", "Delivery Date"].map(header => (
                  <th key={header} style={{ padding: "10px", textAlign: "left", borderBottom: "1px solid #ddd", fontWeight: "bold", whiteSpace: 'nowrap' }}>
                    {header === "Inbox" ? <GrInbox style={{ fontSize: "20px" }} /> : header === "Attachment" ? <GrAttachment style={{ fontSize: "20px" }} /> : header}
                  </th>
                ))}
              </tr>
            </thead>
            {/* Table Body - Shows only paginated cases */}
            <tbody>
              {paginatedCases.length > 0 ? (
                paginatedCases.map(caseItem => {
                  const invoiceData = caseItem.invoiceNumber ? {
                    invoiceNumber: caseItem.invoiceNumber,
                    invoiceTotal: caseItem.invoiceTotal,
                    caseId: caseItem.caseNumber,
                    invoiceDate: caseItem.invoiceData?.invoiceDate
                  } : null;
                  return (
                    <tr key={caseItem._id} style={{ borderBottom: "1px solid #ddd" }}>
                      <td style={{ padding: "10px", textAlign: "left" }}><input type="checkbox" /></td>
                      <td style={{ padding: "10px", textAlign: "left" }}><GrInbox style={{ fontSize: "20px" }} /></td>
                      <td style={{ padding: "10px", textAlign: "left" }}><GrAttachment style={{ fontSize: "20px" }} /></td>
                      <td style={{ padding: "10px", textAlign: "left", position: "relative" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <div style={{ display: "flex", flexDirection: "column" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <Link to={`/case/${caseItem.caseNumber || caseItem.case_id}`} style={{ textDecoration: "none", color: "#004aad", fontSize: "17px" }}>
                                #{caseItem.caseNumber || caseItem.case_id}
                              </Link>
                              <button
                                style={{ fontSize: "18px", background: "transparent", border: "none", cursor: "pointer", padding: 0 }}
                                onClick={(e) => { e.stopPropagation(); toggleDropdown(caseItem._id); }}
                              >
                                <IoSettingsOutline style={{ color: "#004aad", fontSize: "22px" }} />
                              </button>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                              <p style={{ margin: 0 }}>Pan Color:</p>
                              <span
                                style={{
                                  width: "12px",
                                  height: "12px",
                                  backgroundColor: caseItem.color ? caseItem.color.toLowerCase() : "#ccc",
                                  display: "inline-block",
                                  borderRadius: "3px",
                                }}
                              ></span>
                              <span>{caseItem.color || "N/A"}</span>
                            </div>
                            {caseItem.procedures && caseItem.procedures.length > 0 ? (
                              <div style={{ fontSize: "14px" }}>
                                <ul style={{ listStyleType: 'none', paddingLeft: 0 }}>
                                  {caseItem.procedures.map((proc, index) => (
                                    <li key={index} style={{ marginBottom: '5px' }}>
                                      {proc.procedure}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ) : (
                              <div style={{ fontSize: "14px", color: "#666" }}>No procedures selected</div>
                            )}
                          </div>
                        </div>
                        {dropdownVisible === caseItem._id && (
                          <div style={{ position: "absolute", top: "100%", left: "50px", backgroundColor: "#fff", border: "1px solid #ccc", padding: "10px", borderRadius: "5px", zIndex: 1000, width: "150px", boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)" }}>
                            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                              <li style={{ padding: "8px 0", cursor: "pointer" }}><BsFileEarmarkCode /> Close/Invoice</li>
                              <li style={{ padding: "8px 0", cursor: "pointer" }}><BsPencilFill /> Add Notes</li>
                              <li style={{ padding: "8px 0", cursor: "pointer" }}><AiOutlineFileAdd /> Upload Files</li>
                            </ul>
                          </div>
                        )}
                      </td>
                      <td style={{ padding: "10px", textAlign: "left", whiteSpace: 'nowrap' }}>
                        {caseItem.invoiceNumber ? (
                          <Link
                            to={`/invoices/${formatInvoiceNumber(caseItem.invoiceNumber)}`}
                            state={{ invoiceData, caseId: caseItem.caseNumber }}
                            style={{ textDecoration: "none", color: "#004aad", fontSize: "16px", display: 'inline-block' }}
                          >
                            #{formatInvoiceNumber(caseItem.invoiceNumber)}
                          </Link>
                        ) : (
                          ""
                        )}
                        {caseItem.invoiceData && (
                          <div style={{ marginTop: '1px', color: '#555', fontSize: '12px' }}>
                            Invoice Date: {formatDate(caseItem.invoiceData.invoiceDate)}
                          </div>
                        )}
                        {(caseItem.totalPrice || caseItem.totalAmount) &&
                         typeof (caseItem.totalPrice || caseItem.totalAmount) === 'number' ? (
                          <div style={{ marginTop: '1px', color: '#555', fontSize: '14px' }}>
                            {formatAmount(caseItem.totalPrice || caseItem.totalAmount)}
                          </div>
                        ) : null}
                      </td>
                      <td style={{ padding: "10px", textAlign: "left" }}>
                        <span
                          onClick={() => handleAccountClick(caseItem.account_name || caseItem.account_holder)}
                          style={{
                            textDecoration: "none",
                            color: "#004aad",
                            fontSize: "16px",
                            cursor: "pointer",
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {caseItem.account_name || caseItem.account_holder || "N/A"}
                        </span>
                       </td>
                      <td style={{ padding: "10px", textAlign: "left" }}>{caseItem.provider_name || "N/A"}</td>
                      <td style={{ padding: "10px", textAlign: "left" }}>
                        {caseItem.last_name || caseItem.first_name || "N/A"}
                       </td>
                      <td style={{ padding: "10px", textAlign: "left" }}>
                        {caseItem.start_date ? new Date(caseItem.start_date).toLocaleDateString() : "N/A"}
                       </td>
                      <td style={{ padding: "10px", textAlign: "left" }}>
                        {caseItem.arrive_date ? new Date(caseItem.arrive_date).toLocaleDateString() : "N/A"}
                       </td>
                      <td style={{ padding: "10px", textAlign: "left" }}>
                        {caseItem.delivery_date ? new Date(caseItem.delivery_date).toLocaleDateString() : "N/A"}
                       </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="11" style={{ textAlign: "center", padding: "20px" }}>
                    {allCases.length === 0 ? (
                      <div>
                        <p>No cases found for Lab: {userLabId}</p>
                        <p style={{ fontSize: '14px', color: '#666' }}>
                          Make sure you have selected the correct lab and there are cases associated with it.
                        </p>
                      </div>
                    ) : (
                      <div>
                        <p>No cases match the current filters.</p>
                        <p style={{ fontSize: '14px', color: '#666' }}>
                          <strong>Note:</strong> You have {allCases.length} total cases for Lab: {userLabId}.
                          <br />
                          <strong>Try this:</strong>
                          <br />1. Click "Next Month" until you see current month
                          <br />2. Or click "Filters" and change Date Range to "Custom Date Range"
                          <br />3. Or reset filters using the "Reset" button in filter modal
                        </p>
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <br />
        {/* Pagination - Like your image: First, Previous, 1, 2, 3, 4, 5, Next, Last, Page X of Y */}
        {cases.length > itemsPerPage && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            marginTop: '20px', 
            gap: '10px',
            flexWrap: 'wrap'
          }}>
            <Button 
              onClick={handleFirstPage} 
              disabled={currentPage === 1}
              style={{ 
                padding: '8px 16px',
                backgroundColor: currentPage === 1 ? "#f0f0f0" : "#004aad", 
                borderColor: "#004aad", 
                color: currentPage === 1 ? "#999" : "white", 
                borderRadius: '5px', 
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                border: '1px solid #004aad'
              }}
            >
              First
            </Button>
            
            <Button 
              onClick={handlePreviousPage} 
              disabled={currentPage === 1}
              style={{ 
                padding: '8px 16px',
                backgroundColor: currentPage === 1 ? "#f0f0f0" : "#004aad", 
                borderColor: "#004aad", 
                color: currentPage === 1 ? "#999" : "white", 
                borderRadius: '5px', 
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                border: '1px solid #004aad'
              }}
            >
              Previous
            </Button>
            
            {getPageNumbers().map(pageNumber => (
              <Button
                key={pageNumber}
                onClick={() => setCurrentPage(pageNumber)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: currentPage === pageNumber ? "#004aad" : "white",
                  borderColor: "#004aad",
                  color: currentPage === pageNumber ? "white" : "#004aad",
                  borderRadius: '5px',
                  cursor: 'pointer',
                  border: '1px solid #004aad'
                }}
              >
                {pageNumber}
              </Button>
            ))}
            
            <Button 
              onClick={handleNextPage} 
              disabled={currentPage === totalPages}
              style={{ 
                padding: '8px 16px',
                backgroundColor: currentPage === totalPages ? "#f0f0f0" : "#004aad", 
                borderColor: "#004aad", 
                color: currentPage === totalPages ? "#999" : "white", 
                borderRadius: '5px', 
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                border: '1px solid #004aad'
              }}
            >
              Next
            </Button>
            
            <Button 
              onClick={handleLastPage} 
              disabled={currentPage === totalPages}
              style={{ 
                padding: '8px 16px',
                backgroundColor: currentPage === totalPages ? "#f0f0f0" : "#004aad", 
                borderColor: "#004aad", 
                color: currentPage === totalPages ? "#999" : "white", 
                borderRadius: '5px', 
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                border: '1px solid #004aad'
              }}
            >
              Last
            </Button>
            
            <span style={{ 
              padding: '8px 16px', 
              backgroundColor: "#f8f9fa", 
              borderRadius: '5px',
              color: "#004aad",
              fontWeight: 'bold',
              border: '1px solid #004aad'
            }}>
              Page {currentPage} of {totalPages}
            </span>
          </div>
        )}
      </div>
      {/* Filter Modal */}
      <Modal show={showFilterModal} onHide={() => setShowFilterModal(false)} size="lg" centered>
        <Modal.Header style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
          <Modal.Title style={{ fontWeight: 'bold', color: '#004aad' }}>Change Filters</Modal.Title>
          <button onClick={() => setShowFilterModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem' }}>
            <LuX />
          </button>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '20px' }}>
              <div style={{ flex: '1', minWidth: '250px' }}>
                <h5 style={{ color: '#004aad', marginBottom: '15px' }}>Filters</h5>
                <Form.Group controlId="filterType" style={{ marginBottom: '15px' }}>
                  <Form.Label style={{ fontWeight: 'bold' }}>Filter By</Form.Label>
                  <Form.Select
                    name="filterType"
                    value={filters.filterType}
                    onChange={handleFilterChange}
                    style={{ borderRadius: '5px', borderColor: '#ced4da' }}
                  >
                    <option value="account">Account</option>
                    <option value="provider">Provider</option>
                    <option value="patient">Patient</option>
                    <option value="caseId">Case ID</option>
                    <option value="delivery">Delivery Date</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group controlId="filterValue" style={{ marginBottom: '15px' }}>
                  <Form.Label style={{ fontWeight: 'bold' }}>
                    {filters.filterType === 'account' ? 'Account' :
                     filters.filterType === 'provider' ? 'Provider' :
                     filters.filterType === 'patient' ? 'Patient' :
                     filters.filterType === 'caseId' ? 'Case ID' : 'Delivery Date'}
                  </Form.Label>
                  <Form.Select
                    name="filterValue"
                    value={filters.filterValue}
                    onChange={handleFilterChange}
                    style={{ borderRadius: '5px', borderColor: '#ced4da' }}
                    disabled={getCurrentFilterOptions().length === 0}
                  >
                    <option value="">All {filters.filterType === 'account' ? 'Accounts' :
                                      filters.filterType === 'provider' ? 'Providers' :
                                      filters.filterType === 'patient' ? 'Patients' :
                                      filters.filterType === 'caseId' ? 'Case IDs' : 'Delivery Dates'}
                                      ({getCurrentFilterOptions().length})
                    </option>
                    {getCurrentFilterOptions().map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </Form.Select>
                  {getCurrentFilterOptions().length === 0 && (
                    <Form.Text className="text-muted">
                      No {filters.filterType === 'account' ? 'accounts' :
                           filters.filterType === 'provider' ? 'providers' :
                           filters.filterType === 'patient' ? 'patients' :
                           filters.filterType === 'caseId' ? 'case IDs' : 'delivery dates'} found in filtered cases
                    </Form.Text>
                  )}
                </Form.Group>
                <Form.Group controlId="filterBy" style={{ marginBottom: '15px' }}>
                  <Form.Label style={{ fontWeight: 'bold' }}>Date Range</Form.Label>
                  <Form.Select
                    name="by"
                    value={filters.by}
                    onChange={handleFilterChange}
                    style={{ borderRadius: '5px', borderColor: '#ced4da' }}
                  >
                    <option value="month">Current Month ({monthName} {currentYear})</option>
                    <option value="dateRange">Custom Date Range</option>
                  </Form.Select>
                </Form.Group>
                {filters.by === 'dateRange' && (
                  <>
                    <Form.Group controlId="filterStartDate" style={{ marginBottom: '15px' }}>
                      <Form.Label style={{ fontWeight: 'bold' }}>Start Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="startDate"
                        value={filters.startDate}
                        onChange={handleFilterChange}
                        style={{ borderRadius: '5px', borderColor: '#ced4da' }}
                      />
                    </Form.Group>
                    <Form.Group controlId="filterEndDate">
                      <Form.Label style={{ fontWeight: 'bold' }}>End Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="endDate"
                        value={filters.endDate}
                        onChange={handleFilterChange}
                        style={{ borderRadius: '5px', borderColor: '#ced4da' }}
                      />
                    </Form.Group>
                  </>
                )}
              </div>
              <div style={{ flex: '1', minWidth: '250px' }}>
                <h5 style={{ color: '#004aad', marginBottom: '15px' }}>Case Status Options</h5>
                <Form.Group controlId="filterCaseTag" style={{ marginBottom: '15px' }}>
                  <Form.Label style={{ fontWeight: 'bold' }}>Case Tag</Form.Label>
                  <Form.Select
                    name="caseTag"
                    value={filters.caseTag}
                    onChange={handleFilterChange}
                    style={{ borderRadius: '5px', borderColor: '#ced4da' }}
                    disabled={filterOptions.caseTags.length === 0}
                  >
                    <option value="any">Any ({filterOptions.caseTags.length} tags available)</option>
                    {filterOptions.caseTags.map(tag => (
                      <option key={tag} value={tag}>{tag}</option>
                    ))}
                  </Form.Select>
                  {filterOptions.caseTags.length === 0 && (
                    <Form.Text className="text-muted">No case tags found in filtered cases</Form.Text>
                  )}
                </Form.Group>
                <Form.Group controlId="filterCaseStatus" style={{ marginBottom: '15px' }}>
                  <Form.Label style={{ fontWeight: 'bold' }}>Case Status</Form.Label>
                  <Form.Select
                    name="caseStatus"
                    value={filters.caseStatus}
                    onChange={handleFilterChange}
                    style={{ borderRadius: '5px', borderColor: '#ced4da' }}
                    disabled={filterOptions.statuses.length === 0}
                  >
                    <option value="">All Statuses ({filterOptions.statuses.length} available)</option>
                    {filterOptions.statuses.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </Form.Select>
                  {filterOptions.statuses.length === 0 && (
                    <Form.Text className="text-muted">No statuses found in filtered cases</Form.Text>
                  )}
                </Form.Group>
                <Form.Group controlId="filterProperties" style={{ marginBottom: '15px' }}>
                  <Form.Label style={{ fontWeight: 'bold' }}>Properties</Form.Label>
                  <Form.Select
                    name="properties"
                    value={filters.properties}
                    onChange={handleFilterChange}
                    style={{ borderRadius: '5px', borderColor: '#ced4da' }}
                  >
                    <option value="all">All</option>
                    <option value="digital">Digital Manufacturing Files</option>
                    <option value="enclosures">Enclosures</option>
                    <option value="both">Digital + Enclosures</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>
            <div className="alert alert-info">
              <small>
                <strong>Filter Info:</strong> Showing filter options from {cases.length} filtered cases.
                <br />
                <strong>Accounts:</strong> {filterOptions.accounts.length} |
                <strong> Providers:</strong> {filterOptions.providers.length} |
                <strong> Patients:</strong> {filterOptions.patients.length} |
                <strong> Statuses:</strong> {filterOptions.statuses.length}
                <br />
                <strong>Lab Filter:</strong> {userLabId ? `Showing only cases from Lab ${userLabId}` : 'No lab selected'}
              </small>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #dee2e6', padding: '15px' }}>
          <Button variant="secondary" onClick={handleResetFilters} style={{ borderRadius: '5px', padding: '8px 15px' }}>
            Reset All Filters
          </Button>
          <Button
            variant="primary"
            onClick={handleApplyFilters}
            style={{ backgroundColor: '#004aad', borderColor: '#004aad', borderRadius: '5px', padding: '8px 15px' }}
          >
            Apply Filters
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CasesOpened;